/**
 * Temporary developer-only watchdog tuning overrides carried over from the BM-3817 spike.
 *
 * Purpose:
 * - preserve `?gpuWatchdog*` tuning for local investigation and validation scenarios;
 * - keep production watchdog behavior free from URL-param debug scaffolding.
 *
 * Removal plan:
 * - this module is expected to be removed once orchestrator wiring and follow-up rollout
 *   work no longer require URL-driven tuning during development.
 */
import { defaultWatchdogConfig, type WatchdogConfig } from "./performanceWatchdog";

export function getWatchdogConfigFromUrl(defaults = defaultWatchdogConfig): Partial<WatchdogConfig> {
    // In Vitest, import.meta.env.DEV is always true, so the production branch below
    // is structurally untestable in unit tests.
    if (!import.meta.env.DEV) {
        return {};
    }

    // gpuWatchdogFpsTarget is intentionally absent here because it overrides
    // framerateTarget (a PerformanceWatchdog constructor option, not WatchdogConfig).
    const search = new URLSearchParams(window.location.search);
    const parsed: Partial<WatchdogConfig> = {};

    const sampleIntervalMs = toNumber(search.get("gpuWatchdogSampleIntervalMs"));
    const windowSec = toNumber(search.get("gpuWatchdogWindowSec"));
    const fpsRatio = toNumber(search.get("gpuWatchdogFpsRatio"));
    const breachRatio = toNumber(search.get("gpuWatchdogBreachRatio"));
    const qualityFloorRatio = toNumber(search.get("gpuWatchdogQualityFloorRatio"));
    const warmupSec = toNumber(search.get("gpuWatchdogWarmupSec"));
    const resolutionFloor = toNumber(search.get("gpuWatchdogResolutionFloor"));
    const resolutionEpsilon = toNumber(search.get("gpuWatchdogResolutionEpsilon"));
    const primitiveStableSamples = toNumber(search.get("gpuWatchdogPrimitiveStableSamples"));
    const primitiveStableDelta = toNumber(search.get("gpuWatchdogPrimitiveStableDelta"));

    if (sampleIntervalMs !== undefined && sampleIntervalMs > 0) {
        parsed.sampleIntervalMs = sampleIntervalMs;
    }
    if (windowSec !== undefined && windowSec > 0) {
        parsed.windowSec = windowSec;
    }
    if (fpsRatio !== undefined && fpsRatio > 0 && fpsRatio < 1) {
        parsed.fpsRatio = fpsRatio;
    }
    if (breachRatio !== undefined && breachRatio > 0 && breachRatio <= 1) {
        parsed.breachRatio = breachRatio;
    }
    if (qualityFloorRatio !== undefined && qualityFloorRatio > 0 && qualityFloorRatio <= 1) {
        parsed.qualityFloorRatio = qualityFloorRatio;
    }
    if (warmupSec !== undefined && warmupSec >= 0) {
        parsed.warmupSec = warmupSec;
    }
    if (resolutionFloor !== undefined && resolutionFloor >= 0) {
        parsed.resolutionFloor = resolutionFloor;
    }
    if (resolutionEpsilon !== undefined && resolutionEpsilon >= 0) {
        parsed.resolutionEpsilon = resolutionEpsilon;
    }
    if (primitiveStableSamples !== undefined && primitiveStableSamples > 0) {
        parsed.primitiveStableSamples = primitiveStableSamples;
    }
    if (primitiveStableDelta !== undefined && primitiveStableDelta >= 0) {
        parsed.primitiveStableDelta = primitiveStableDelta;
    }

    return {
        ...defaults,
        ...parsed,
    };
}

function toNumber(value: string | null): number | undefined {
    if (!value) {
        return undefined;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
}
